import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import { protocolService } from '../../services/protocolService';
import { useAuth } from '../../contexts/AuthContext';
import '../../assets/css/Research.css';

const ProgramAdminEditProtocol = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    show_ai_response: false
  });
  
  // Component state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [protocol, setProtocol] = useState(null);
  const [linkedQuestions, setLinkedQuestions] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  // Load protocol data when the component mounts
  useEffect(() => {
    if (id) {
      loadProtocol(id);
    } else {
      setError('No protocol ID provided');
      setLoading(false);
    }
  }, [id]);

  const loadProtocol = async (protocolId) => {
    try {
      setLoading(true);
      const response = await protocolService.getProtocol(protocolId, true);
      
      if (!response.protocol) {
        throw new Error('Protocol not found');
      }
      
      // Check if the user is authorized to edit this protocol
      if (user.role !== 'program_admin' && response.protocol.created_by !== user.id) {
        throw new Error('You are not authorized to edit this protocol');
      }
      
      setProtocol(response.protocol);
      setFormData({
        name: response.protocol.name || '',
        description: response.protocol.description || '',
        is_active: response.protocol.is_active !== false,
        show_ai_response: response.protocol.show_ai_response === true
      });
      
      setLinkedQuestions(response.protocol.questions || []);
    } catch (error) {
      console.error('Error loading protocol:', error);
      setError(error.message || 'Failed to load protocol');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Update protocol using API
      const response = await protocolService.updateProtocol(id, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        is_active: formData.is_active,
        show_ai_response: formData.show_ai_response
      });

      if (response.success) {
        // Show success notification
        setShowSuccess(true);
        
        // Hide success message after 2 seconds and redirect
        setTimeout(() => {
          setShowSuccess(false);
          navigate('/program-admin/protocols');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to update protocol');
      }
    } catch (error) {
      console.error('Error updating protocol:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update protocol. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProtocol = async () => {
    try {
      setIsSubmitting(true);
      const response = await protocolService.deleteProtocol(id);
      
      if (response.success) {
        navigate('/program-admin/protocols', { 
          state: { message: 'Protocol deleted successfully', type: 'success' } 
        });
      } else {
        throw new Error(response.message || 'Failed to delete protocol');
      }
    } catch (error) {
      console.error('Error deleting protocol:', error);
      setError(error.message || 'Failed to delete protocol');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/program-admin/protocols');
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Edit Protocol">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading protocol data...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (error) {
    return (
      <ResearchLayout pageTitle="Edit Protocol">
        <div className="container-fluid">
          <div className="alert alert-danger mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-3">
              <button 
                className="btn btn-outline-danger" 
                onClick={() => navigate('/program-admin/protocols')}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Return to Protocols
              </button>
            </div>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Edit Protocol">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> Protocol updated successfully.
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/program-admin/dashboard" className="text-decoration-none">
                    <i className="fas fa-home me-1"></i>
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/program-admin/protocols" className="text-decoration-none">
                    <i className="fas fa-clipboard-list me-1"></i>
                    Protocols
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Edit Protocol
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-edit me-2"></i>
                  Edit Protocol
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="name" className="form-label fw-bold">
                      <i className="fas fa-clipboard-list me-2 text-primary"></i>
                      Protocol Name *
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter protocol name"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="description" className="form-label fw-bold">
                      <i className="fas fa-align-left me-2 text-primary"></i>
                      Protocol Description *
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      rows="5"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Provide a detailed description of the protocol..."
                      required
                    ></textarea>
                  </div>


                  <div className="mb-4">
                    <label htmlFor="show_ai_response" className="form-label fw-bold">
                      <i className="fas fa-robot me-2 text-primary"></i>
                      AI Response Visibility *
                    </label>
                    <select
                      className="form-select"
                      id="show_ai_response"
                      name="show_ai_response"
                      value={formData.show_ai_response ? 'true' : 'false'}
                      onChange={e => setFormData(prev => ({ ...prev, show_ai_response: e.target.value === 'true' }))}
                      required
                    >
                      <option value="true">View AI response (Doctors will see AI assessment)</option>
                      <option value="false">Hide AI response (Doctors will NOT see AI assessment)</option>
                    </select>
                    <div className="form-text">
                      Choose whether doctors can view the AI assessment when answering questions in this protocol.
                    </div>
                  </div>

                  <div className="mb-4 form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleCheckboxChange}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Protocol is active
                    </label>
                    <div className="form-text">
                      Inactive protocols cannot be assigned to doctors.
                    </div>
                  </div>

                  {linkedQuestions.length > 0 && (
                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        <i className="fas fa-question-circle me-2 text-info"></i>
                        Linked Questions ({linkedQuestions.length})
                      </label>
                      <div className="card bg-light">
                        <div className="card-body p-3">
                          <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                            {linkedQuestions.map((question, idx) => (
                              <div key={question.id} className="mb-2 d-flex align-items-center">
                                <span className="badge bg-info me-2">{idx + 1}</span>
                                <div>{question.text}</div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3">
                            <Link to={`/program-admin/link-questions?protocol=${id}`} className="btn btn-sm btn-outline-info">
                              <i className="fas fa-link me-1"></i>
                              Manage Questions
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {linkedQuestions.length === 0 && (
                    <div className="mb-4">
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        This protocol has no linked questions yet. 
                        <Link to={`/program-admin/link-questions?protocol=${id}`} className="alert-link ms-2">
                          Link questions now
                        </Link>
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-danger me-2"
                        onClick={() => setDeleteConfirmation(true)}
                        disabled={isSubmitting}
                      >
                        <i className="fas fa-trash-alt me-2"></i>
                        Delete Protocol
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                      >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                      </button>
                    </div>
                    
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg px-4"
                      disabled={isSubmitting || !formData.name.trim() || !formData.description.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Update Protocol
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Created/Updated Info Card */}
        {protocol && (
          <div className="row justify-content-center mt-4">
            <div className="col-lg-8 col-md-10">
              <div className="card bg-light border-0">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">
                        <i className="fas fa-user me-1"></i>
                        Created by: {protocol.created_by_name || 'Unknown'}
                      </small>
                    </div>
                    <div className="col-md-6 mb-2">
                      <small className="text-muted">
                        <i className="fas fa-calendar-plus me-1"></i>
                        Created on: {new Date(protocol.created_at).toLocaleDateString()}
                      </small>
                    </div>
                    {protocol.updated_at && (
                      <>
                        <div className="col-md-6">
                          <small className="text-muted">
                            <i className="fas fa-user-edit me-1"></i>
                            Last modified by: {protocol.updated_by_name || 'Unknown'}
                          </small>
                        </div>
                        <div className="col-md-6">
                          <small className="text-muted">
                            <i className="fas fa-calendar-check me-1"></i>
                            Last modified on: {new Date(protocol.updated_at).toLocaleDateString()}
                          </small>
                        </div>
                      </>
                    )}
                    <div className="col-md-12 mt-2">
                      <span className={`badge ${protocol.show_ai_response ? 'bg-info' : 'bg-secondary'}`}
                        title={protocol.show_ai_response ? 'Doctors can view AI assessment' : 'Doctors cannot view AI assessment'}>
                        <i className="fas fa-robot me-1"></i>
                        {protocol.show_ai_response ? 'AI Viewed' : 'AI Hidden'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmation && (
          <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Confirm Protocol Deletion
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setDeleteConfirmation(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">Are you sure you want to delete this protocol? This action cannot be undone.</p>
                  
                  {linkedQuestions.length > 0 && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      This protocol has {linkedQuestions.length} linked questions that will be unlinked.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setDeleteConfirmation(false)}
                    disabled={isSubmitting}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger" 
                    onClick={handleDeleteProtocol}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt me-2"></i>
                        Delete Protocol
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminEditProtocol;